import {
  Button,
  Card,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  KeyOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  selectAccessToken,
  selectIdToken,
} from "../../features/access-control/accountSlice";
import {
  WebAuthnCredentialSummary,
  webauthnDeleteCredentialServer,
  webauthnListCredentialsServer,
  webauthnRegisterCompleteServer,
  webauthnRegisterStartServer,
} from "../../features/access-control/accountAPI";
import {
  isWebAuthnSupported,
  serializeRegistrationCredential,
  toCredentialCreationOptions,
} from "../../features/access-control/webauthn";

export default function SecurityPage() {
  const idToken = useAppSelector(selectIdToken);
  const accessToken = useAppSelector(selectAccessToken);
  const [credentials, setCredentials] = useState<WebAuthnCredentialSummary[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [passkeyName, setPasskeyName] = useState("");

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const res = await webauthnListCredentialsServer({ idToken, accessToken });
      setCredentials(res.credentials || []);
    } catch {
      message.error("Could not load passkeys.");
    } finally {
      setLoading(false);
    }
  }, [idToken, accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addPasskey = async () => {
    setNameModalOpen(false);
    setAdding(true);
    try {
      const { credentialCreationOptions } = await webauthnRegisterStartServer({
        idToken,
        accessToken,
      });
      const options = toCredentialCreationOptions(
        credentialCreationOptions as { publicKey?: Record<string, unknown> }
      );
      const credential = (await navigator.credentials.create(
        options
      )) as PublicKeyCredential | null;
      if (!credential) return;
      await webauthnRegisterCompleteServer({
        idToken,
        accessToken,
        credential: serializeRegistrationCredential(credential),
        friendlyCredentialName: passkeyName || undefined,
      });
      message.success("Passkey added.");
      setPasskeyName("");
      await refresh();
    } catch {
      message.error("Could not add passkey.");
    } finally {
      setAdding(false);
    }
  };

  const removePasskey = async (credentialId: string) => {
    try {
      await webauthnDeleteCredentialServer({
        idToken,
        accessToken,
        credentialId,
      });
      message.success("Passkey removed.");
      await refresh();
    } catch {
      message.error("Could not remove passkey.");
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "24px auto", padding: "0 16px" }}>
      <Typography.Title level={3}>Security</Typography.Title>
      <Card
        title={
          <Space>
            <KeyOutlined /> Passkeys
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={adding}
            disabled={!isWebAuthnSupported() || !accessToken}
            onClick={() => setNameModalOpen(true)}
          >
            Add passkey
          </Button>
        }
      >
        <Typography.Paragraph type="secondary">
          Passkeys let you sign in without a password. When you have a passkey,
          sign-in asks for it first.
        </Typography.Paragraph>
        <List
          loading={loading}
          locale={{ emptyText: "No passkeys yet." }}
          dataSource={credentials}
          renderItem={(c) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="del"
                  title="Remove this passkey?"
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => removePasskey(c.credentialId)}
                >
                  <Button danger type="text" icon={<DeleteOutlined />}>
                    Remove
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<KeyOutlined />}
                title={c.friendlyCredentialName || "Passkey"}
                description={
                  c.createdAt
                    ? `Added ${new Date(c.createdAt).toLocaleString()}`
                    : undefined
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Card
        title={
          <Space>
            <SafetyCertificateOutlined /> Authenticator app (TOTP)
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        <Typography.Paragraph type="secondary">
          Your authenticator app remains available as a fallback. With no
          passkey, sign-in uses your password and a TOTP code.
        </Typography.Paragraph>
        <Tag color="green">Enabled</Tag>
      </Card>

      <Modal
        title="Name this passkey"
        open={nameModalOpen}
        onOk={addPasskey}
        okText="Continue"
        onCancel={() => setNameModalOpen(false)}
      >
        <Input
          placeholder="e.g. MacBook Touch ID"
          value={passkeyName}
          onChange={(e) => setPasskeyName(e.target.value)}
          onPressEnter={addPasskey}
        />
      </Modal>
    </div>
  );
}
